/**
 * defaults.ts
 * Bundled default game content — the offline source of truth. The
 * ContentRepository starts from these values and overlays any rows found in
 * Supabase, so the game is always playable (and tunable in code) even with no
 * database, and live-tunable from the DB once it's connected.
 */

import type { BalanceValues, GameContent, LevelDefinition, SpellDefinition } from '../../types';
import { SPELLS } from '../../systems/SpellSystem';
import { LEVEL_01 } from '../../campaign/levels/level_01';
import { FUTURE_SONG_TITLES } from '../futureSongs';
import {
  FOG_EXPLORED_ALPHA,
  FOG_UNEXPLORED_ALPHA,
  GOOD_BEACON_HEAL_RATIO,
  GOOD_BEACON_PULSE_MIN,
  GOOD_BEACON_PULSE_MAX,
  BADDIE_BEACON_BUILD_TIME,
  BADDIE_BEACON_HP,
  BADDIE_BEACON_JAMMING,
  BADDIE_BEACON_PULSE_TIME,
  STARTING_REPUTATION,
  STARTING_FANBASE,
  STARTING_SALES,
  STARTING_GEAR,
  BEACON_STARTING_SIGNAL,
  SONG_DROP_THRESHOLD,
  TEMPO_DEFAULT,
  UNIT_ATTACK_RATE,
  UNIT_SIGHT_RADIUS,
  UNIT_ATTACK_RADIUS,
  UNIT_HP_PROTECTOR,
  UNIT_HP_PROMOTER,
  UNIT_HP_ENGINE,
  UNIT_HP_SUSTAIN,
  UNIT_HP_MYSTIC,
  UNIT_HP_SONGWRITER,
  UNIT_MOVE_SPEED,
  UNIT_MOVE_SPEED_PROMOTER,
  UNIT_MOVE_SPEED_MYSTIC,
  UNIT_MOVE_SPEED_SONGWRITER,
  TEMPO_DECAY_RATE,
  TEMPO_ENEMY_HIT,
  RES_REP_BASE_INCOME,
  RES_SALES_BASE_INCOME,
  RES_SALES_FAN_MULT,
  KILL_REP_SWARM,
  KILL_SALES_SWARM,
  KILL_REP_MOGUL,
  KILL_SALES_MOGUL,
  VENUE_REP_DIRTY_DUCK,
  VENUE_SALES_DIRTY_DUCK,
  VENUE_SALES_RECORD_SHOP,
  VENUE_FAN_HALL,
  VENUE_REP_INDIE,
  VENUE_SALES_INDIE,
  VENUE_REP_ROOFTOP_STAGE,
  VENUE_FAN_ROOFTOP_STAGE,
  VENUE_SALES_ARTS_CENTRE,
  VENUE_FAN_ARTS_CENTRE,
  VENUE_REP_UNDERGROUND,
  VENUE_SALES_UNDERGROUND,
  VENUE_REP_TOWN_HALL,
  VENUE_FAN_TOWN_HALL,
  UNIT_DAMAGE,
  ENEMY_HP_MOGUL,
  ENEMY_HP_SWARM,
  ENEMY_HP_BAD_MANAGER,
  ENEMY_HP_TRIBUTE,
  ENEMY_HP_MUSIC_PRESS,
  ENEMY_HP_PIRATE,
  ENEMY_DAMAGE_MOGUL,
  ENEMY_DAMAGE_SWARM,
  ENEMY_DAMAGE_BAD_MANAGER,
  ENEMY_DAMAGE_TRIBUTE,
  ENEMY_DAMAGE_MUSIC_PRESS,
  ENEMY_DAMAGE_PIRATE,
  ENEMY_MOVE_SPEED_MOGUL,
  ENEMY_MOVE_SPEED_SWARM,
  ENEMY_MOVE_SPEED_BAD_MANAGER,
  ENEMY_MOVE_SPEED_TRIBUTE,
  ENEMY_MOVE_SPEED_MUSIC_PRESS,
  ENEMY_MOVE_SPEED_PIRATE,
  ENEMY_ATTACK_RATE,
  ENEMY_SIGHT_RADIUS,
  ENEMY_ATTACK_RADIUS,
} from '../../constants';

export const DEFAULT_BALANCE: BalanceValues = {
  startingReputation: STARTING_REPUTATION,
  startingFanbase: STARTING_FANBASE,
  startingSales: STARTING_SALES,
  startingGear: STARTING_GEAR,
  beaconStartingSignal: BEACON_STARTING_SIGNAL,
  songDropThreshold: SONG_DROP_THRESHOLD,
  tempoDefault: TEMPO_DEFAULT,
  unitAttackRate: UNIT_ATTACK_RATE,
  unitSightRadius: UNIT_SIGHT_RADIUS,
  unitAttackRadius: UNIT_ATTACK_RADIUS,
  unitHpProtector:  UNIT_HP_PROTECTOR,
  unitHpPromoter:   UNIT_HP_PROMOTER,
  unitHpEngine:     UNIT_HP_ENGINE,
  unitHpSustain:    UNIT_HP_SUSTAIN,
  unitHpMystic:     UNIT_HP_MYSTIC,
  unitHpSongwriter: UNIT_HP_SONGWRITER,
  unitMoveSpeed:            UNIT_MOVE_SPEED,
  unitMoveSpeedPromoter:    UNIT_MOVE_SPEED_PROMOTER,
  unitMoveSpeedMystic:      UNIT_MOVE_SPEED_MYSTIC,
  unitMoveSpeedSongwriter:  UNIT_MOVE_SPEED_SONGWRITER,
  tempoDecayRate:         TEMPO_DECAY_RATE,
  tempoEnemyHit:          TEMPO_ENEMY_HIT,

  resRepBaseIncome:   RES_REP_BASE_INCOME,
  resSalesBaseIncome: RES_SALES_BASE_INCOME,
  resSalesFanMult:    RES_SALES_FAN_MULT,

  killRepSwarm:   KILL_REP_SWARM,
  killSalesSwarm: KILL_SALES_SWARM,
  killRepMogul:   KILL_REP_MOGUL,
  killSalesMogul: KILL_SALES_MOGUL,

  venueRepDirtyDuck:    VENUE_REP_DIRTY_DUCK,
  venueSalesDirtyDuck:  VENUE_SALES_DIRTY_DUCK,
  venueSalesRecordShop: VENUE_SALES_RECORD_SHOP,
  venueFanHall:         VENUE_FAN_HALL,
  venueRepIndie:        VENUE_REP_INDIE,
  venueSalesIndie:      VENUE_SALES_INDIE,
  venueRepRooftopStage:  VENUE_REP_ROOFTOP_STAGE,
  venueFanRooftopStage:  VENUE_FAN_ROOFTOP_STAGE,
  venueSalesArtsCentre:  VENUE_SALES_ARTS_CENTRE,
  venueFanArtsCentre:    VENUE_FAN_ARTS_CENTRE,
  venueRepUnderground:   VENUE_REP_UNDERGROUND,
  venueSalesUnderground: VENUE_SALES_UNDERGROUND,
  venueRepTownHall:      VENUE_REP_TOWN_HALL,
  venueFanTownHall:      VENUE_FAN_TOWN_HALL,

  unitDamage: UNIT_DAMAGE,

  enemyHpMogul:      ENEMY_HP_MOGUL,
  enemyHpSwarm:      ENEMY_HP_SWARM,
  enemyHpBadManager: ENEMY_HP_BAD_MANAGER,
  enemyHpTribute:    ENEMY_HP_TRIBUTE,
  enemyHpMusicPress: ENEMY_HP_MUSIC_PRESS,
  enemyHpPirate:     ENEMY_HP_PIRATE,

  enemyDamageMogul:      ENEMY_DAMAGE_MOGUL,
  enemyDamageSwarm:      ENEMY_DAMAGE_SWARM,
  enemyDamageBadManager: ENEMY_DAMAGE_BAD_MANAGER,
  enemyDamageTribute:    ENEMY_DAMAGE_TRIBUTE,
  enemyDamageMusicPress: ENEMY_DAMAGE_MUSIC_PRESS,
  enemyDamagePirate:     ENEMY_DAMAGE_PIRATE,

  enemyMoveSpeedMogul:      ENEMY_MOVE_SPEED_MOGUL,
  enemyMoveSpeedSwarm:      ENEMY_MOVE_SPEED_SWARM,
  enemyMoveSpeedBadManager: ENEMY_MOVE_SPEED_BAD_MANAGER,
  enemyMoveSpeedTribute:    ENEMY_MOVE_SPEED_TRIBUTE,
  enemyMoveSpeedMusicPress: ENEMY_MOVE_SPEED_MUSIC_PRESS,
  enemyMoveSpeedPirate:     ENEMY_MOVE_SPEED_PIRATE,

  enemyAttackRate:   ENEMY_ATTACK_RATE,
  enemySightRadius:  ENEMY_SIGHT_RADIUS,
  enemyAttackRadius: ENEMY_ATTACK_RADIUS,

  fogExploredAlpha:   FOG_EXPLORED_ALPHA,
  fogUnexploredAlpha: FOG_UNEXPLORED_ALPHA,

  goodBeaconHealRatio: GOOD_BEACON_HEAL_RATIO,
  goodBeaconPulseMin:  GOOD_BEACON_PULSE_MIN,
  goodBeaconPulseMax:  GOOD_BEACON_PULSE_MAX,

  baddieBeaconBuildTime: BADDIE_BEACON_BUILD_TIME,
  baddieBeaconHp:        BADDIE_BEACON_HP,
  baddieBeaconJamming:   BADDIE_BEACON_JAMMING,
  baddieBeaconPulseTime: BADDIE_BEACON_PULSE_TIME,
};

export const DEFAULT_SPELLS: SpellDefinition[] = SPELLS.map(spell => ({
  id: spell.key,
  name: spell.name,
  cooldown: spell.cooldown,
  reputationCost: 0,
  description: spell.desc,
}));

export const DEFAULT_LEVELS: LevelDefinition[] = [LEVEL_01];

export const DEFAULT_CONTENT: GameContent = {
  source: 'defaults',
  balance: DEFAULT_BALANCE,
  spells: DEFAULT_SPELLS,
  levels: DEFAULT_LEVELS,
  futureSongs: FUTURE_SONG_TITLES.map(title => ({ title })),
};
